import * as microsoftTeams from '@microsoft/teams-js';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getUrl } from './api';
import { QueueOrSquadId } from './model';
import { actions } from './store';

export type Theme = 'light' | 'dark' | 'contrast'
export const DefaultTheme: Theme = 'light'

type MyTeamsContext = TeamsContextData & {
  init: boolean
  theme: Theme
  achieve?: boolean
  setTheme(theme: Theme): void
  context?: {}
}

type TeamsContextData = ({
  inTeams: false
} | {
  inTeams: true
  settings?: TabSettings
  setSettings(settings: TabSettings, name: string): void
  getToken(): Promise<string>
}) & {
  init: boolean
}

interface TabSettings {
  showId: QueueOrSquadId,
}

const TeamsContext = createContext<MyTeamsContext>({ inTeams: false, init: false, theme: DefaultTheme, setTheme() { } })

const TeamsProvider: React.FC = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(DefaultTheme)
  const [teams, setTeams] = useState<TeamsContextData>({ inTeams: false, init: false })
  const dispatch = useDispatch()

  useEffect(() => {
    if (window.self === window.top) {
      setTeams({ inTeams: false, init: true })
      return
    }

    function updateTheme(theme: string | undefined) {
      if (theme === 'default') { theme = 'light' }
      dispatch(actions.setTheme((theme || DefaultTheme) as Theme))
      //setTheme((theme || DefaultTheme) as Theme)
    }

    microsoftTeams.initialize()
    microsoftTeams.registerOnThemeChangeHandler(theme => updateTheme(theme))
    microsoftTeams.getContext(ctx => {
      const settings = parseEntityId(ctx.entityId)

      updateTheme(ctx.theme)
      setTeams({
        init: true,
        inTeams: true,
        settings,
        setSettings(settings, name) {
          microsoftTeams.settings.registerOnSaveHandler(e => {
            microsoftTeams.settings.setSettings({
              contentUrl: getUrl('', true),
              websiteUrl: getUrl('', true),
              entityId: JSON.stringify(settings),
              suggestedDisplayName: name,

            })

            e.notifySuccess()
          })

          microsoftTeams.settings.setValidityState(true)
        },
        getToken() {
          return new Promise((resolve, reject) => {
            microsoftTeams.authentication.getAuthToken({
              successCallback(token) {
                console.info('successCallback ' + token)
                if (resolve) {
                  resolve(token.toString())
                }
              },
              failureCallback(err) {
                console.error('failureCallback ' + err)
                reject(err)
              },
            })
          })
        },
      })

      dispatch(actions.setInTeams())

      if (settings) {
        dispatch(actions.showSingleQueueOrSquad(settings.showId))

      }
    })
  }, [dispatch])

  return <TeamsContext.Provider value={useMemo(() => ({ ...teams, theme, setTheme }), [teams, theme])}>
    {children}
  </TeamsContext.Provider>
}

function parseEntityId(entityId: string): TabSettings | undefined {
  if (entityId) {
    try {
      const data = JSON.parse(entityId) as TabSettings
      if ('showId' in data) {
        return data
      }
    } catch (e) {
    }
  }
}

export function useTeams() {
  return useContext(TeamsContext)
}

export default TeamsProvider
